//* Import libraries
import { useState, useCallback, useEffect } from "react";

//* Import react-router hooks
import { useNavigate } from "react-router-dom";

//* Import components
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Import custom hooks
import { useDemands } from "../../../hooks/demand/useDemands";

import { Demands } from "../../../hooks/demand/types/types";

var debounce = require("lodash.debounce");

export function DemandsView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [demandToDelete, setDemandToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalDemands, setTotalDemands] = useState<number>(0);

  const {
    demands,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteDemand,
    refetch,
    changePage,
    isDeleting,
  } = useDemands(token);

  useEffect(() => {
    setTotalDemands(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  /**
   * Debounced search function that updates the search state and pagination.
   * 
   * This function uses a debounce mechanism to delay the execution of the search logic
   * until the user has stopped typing for a specified duration (500ms). If the search
   * input value has a length of 3 or more characters after trimming, it updates the
   * search state with the input value. Otherwise, it clears the search state and resets
   * the pagination to the first page.
   * 
   * @param value - The search input value.
   * 
   * Dependencies:
   * - `setSearch`: Function to update the search state.
   * - `setPagination`: Function to update the pagination state.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  /**
   * Handles the change event for the search input.
   * Updates the search input value, sets the searching state to true,
   * and triggers a debounced search with the new input value.
   *
   * @param value - The new value of the search input.
   */
  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  /**
   * Handles the confirmation of deleting a demand.
   * 
   * This function is executed when the user confirms the deletion of a demand.
   * It checks if there is a demand to delete, then attempts to delete it using the `deleteDemand` function.
   * If the current page has only one item and it's not the first page, it changes to the previous page.
   * Finally, it hides the warning and resets the `demandToDelete` state.
   * 
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when the demand is deleted.
   * @throws Will log an error to the console if the deletion fails.
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (demandToDelete) {
      try {
        const currentPageItems = demands.length;
        const currentPage = pagination.currentPage;

        await deleteDemand(demandToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setDemandToDelete(null);
      } catch (error) {
        console.error("Error deleting admin:", error);
      }
    }
  }, [
    demandToDelete,
    deleteDemand,
    demands.length,
    pagination.currentPage,
    changePage,
  ]);

  /**
   * Handles the click action for deleting a demand.
   * Sets the demand to be deleted and shows a warning message.
   *
   * @param {string} id - The ID of the demand to be deleted.
   */
  const handleDeleteActionClick = useCallback((id: string) => {
    setDemandToDelete(id);
    setShowWarning(true);
  }, []);

  /**
   * Handles the change in pagination by updating the current page.
   *
   * @param {number} value - The new page number to switch to.
   */
  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  if (isDeleting) {
    return <Loading data-testid="deleting-spinner" />;
  }

  if (error) {
    return <div data-testid="error-message">Error: {error}</div>;
  }

  return (
    <div className="row" data-testid="demands-view-container">
      <List
        search_input_value={searchInputValue}
        search_text="Pesquise pelo nome da demanda"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Demandas (Total: ${totalDemands})`}
        not_found_message="Nenhum demanda encontrado"
        button_link="/demands/create"
        button_text="Adicionar demanda"
        deleting_message="Ao deletar esta demanda, ela não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/demands/edit/${id}`)}
        onChangePagination={handleChangePagination}
        onManagerActionClick={(value) => {
          navigate(`/demands/${value}`, {
            state: { filter: value, key: "demand_id" },
          });
        }}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={demands.map((demand: Demands) => ({
          ...demand,
          key: demand.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "desc", type: "text", header: "Descrição" },
          { name: "date", type: "date", header: "Data de encerramento" },
          { name: "createdAt", type: "date", header: "Criado em" },
        ]}
        loading_search={isSearching}
        data-testid="demands-list"
      />
    </div>
  );
}
